import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import RegistrationForm from './components/RegistrationForm';
import VerificationSuccess from './components/VerificationSuccess';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, authError, clearAuthError } = useAuth();
  const [registrationStep, setRegistrationStep] = useState('form'); // 'form' | 'verification'
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    // Check URL for authentication tokens (email verification)
    const urlParams = new URLSearchParams(window?.location?.hash?.substring(1));
    const accessToken = urlParams?.get('access_token');
    const type = urlParams?.get('type');

    // If we have auth tokens and user is authenticated, they've verified their email
    if (accessToken && type && user && user?.email_confirmed_at) {
      console.log('Email verification completed, redirecting to profile');
      navigate('/user-profile', { replace: true });
      return;
    }

    // Check if user is already authenticated
    if (user && !loading && user?.email_confirmed_at) {
      navigate('/user-profile', { replace: true });
      return;
    }

    // Preserve any intended actions from location state
    if (location?.state?.intendedAction) {
      localStorage?.setItem('intendedAction', JSON.stringify(location?.state?.intendedAction));
    }

    // Clear any auth errors when component mounts
    if (authError) {
      clearAuthError();
    }
  }, [user, loading, navigate, location?.state, authError, clearAuthError]);

  const handleRegistrationSuccess = () => {
    const registrationData = JSON.parse(localStorage.getItem('registrationData') || '{}');
    setUserEmail(registrationData?.email || '');
    setRegistrationStep('verification');
  };

  const handleVerificationComplete = () => {
    // Clear registration data and navigate to profile
    localStorage.removeItem('registrationData');
    navigate('/user-profile', { replace: true });
  };

  const handleResendVerification = async () => {
    // Simulate API call to resend verification email
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Verification email resent to:', userEmail);
        resolve();
      }, 1000);
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Auth Error Alert */}
      {authError && (
        <div className="bg-destructive/10 border-b border-destructive/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-sm text-destructive font-body">{authError}</p>
              </div>
              <button
                onClick={clearAuthError}
                className="text-destructive hover:text-destructive/80 transition-gentle"
                aria-label="Dismiss error"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="pt-16">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 py-16 lg:py-24">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center opacity-10" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-organic">
                  <Icon name="Leaf" size={24} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-semibold text-foreground">
                    Holistic Directories
                  </h1>
                  <p className="text-sm text-muted-foreground font-caption -mt-1">
                    Connect. Practice. Evolve.
                  </p>
                </div>
              </div>

              {registrationStep === 'form' && (
                <div className="max-w-2xl mx-auto">
                  <h2 className="text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-4">
                    Begin Your Wellness Journey
                  </h2>
                  <p className="text-lg text-muted-foreground font-body">
                    Join thousands of people discovering holistic practitioners and wellness events across the UK
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Registration Content */}
        <div className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <div className="w-full max-w-lg">
                <div className="bg-card rounded-2xl shadow-organic-lg p-8 lg:p-12">
                  {registrationStep === 'form' ? (
                    <RegistrationForm onRegistrationSuccess={handleRegistrationSuccess} />
                  ) : (
                    <VerificationSuccess
                      userEmail={userEmail}
                      onResendVerification={handleResendVerification}
                      onVerificationComplete={handleVerificationComplete}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        {registrationStep === 'form' && (
          <div className="bg-muted py-16 lg:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-heading font-semibold text-foreground mb-4">
                  Why Join Our Community?
                </h3>
                <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
                  Discover the benefits of being part of the UK's leading holistic wellness directory
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon name="Heart" size={28} color="var(--color-primary)" />
                  </div>
                  <h4 className="text-xl font-heading font-semibold text-foreground mb-3">
                    Save Favourites
                  </h4>
                  <p className="text-muted-foreground font-body">
                    Save your favourite practitioners and events to easily find them later and never miss out on wellness opportunities.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon name="MapPin" size={28} color="var(--color-accent)" />
                  </div>
                  <h4 className="text-xl font-heading font-semibold text-foreground mb-3">
                    Local Discovery
                  </h4>
                  <p className="text-muted-foreground font-body">
                    Get personalised recommendations based on your location and discover amazing wellness services near you.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-success/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon name="Calendar" size={28} color="var(--color-success)" />
                  </div>
                  <h4 className="text-xl font-heading font-semibold text-foreground mb-3">
                    Easy Booking
                  </h4>
                  <p className="text-muted-foreground font-body">
                    Book sessions and events directly through our platform with seamless integration to practitioner booking systems.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trust Indicators */}
        <div className="bg-background py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <p className="text-sm text-muted-foreground font-body mb-6">
                Trusted by wellness practitioners and consumers across the UK
              </p>

              <div className="flex items-center justify-center space-x-8 opacity-60">
                <div className="flex items-center space-x-2">
                  <Icon name="Shield" size={16} />
                  <span className="text-xs font-body">Secure & Private</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={16} />
                  <span className="text-xs font-body">5,000+ Members</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Star" size={16} />
                  <span className="text-xs font-body">Verified Practitioners</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-foreground text-background py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Leaf" size={20} color="white" />
              </div>
              <span className="text-lg font-heading font-semibold">Holistic Directories</span>
            </div>
            <p className="text-sm opacity-80 font-body">
              © {new Date()?.getFullYear()} Holistic Directories. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Register;